import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Facebook, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import logoFull from "@/assets/logo-chistera-full.jpg";
import { cn } from "@/lib/utils";

const navItems: { label: string; href: string; type: "hash" | "link" }[] = [
  { label: "CrossFit", href: "#crossfit", type: "hash" },
  { label: "Cours", href: "#cours", type: "hash" },
  { label: "Tarifs", href: "#tarifs", type: "hash" },
  { label: "Box", href: "#box", type: "hash" },
  { label: "Événements", href: "#evenements", type: "hash" },
  { label: "Coaching", href: "#coaching", type: "hash" },
  { label: "FAQ", href: "#faq", type: "hash" },
  { label: "Contact", href: "#contact", type: "hash" },
  { label: "Lexique", href: "/lexique", type: "link" },
  { label: "Mes charges", href: "/charges", type: "link" },
  { label: "RDV Services", href: "/rdv-services", type: "link" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (item: typeof navItems[0]) => {
    setOpen(false);
    if (item.type === "link") {
      navigate(item.href);
    } else if (location.pathname !== "/") {
      navigate("/" + item.href);
    } else {
      const el = document.querySelector(item.href);
      el?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b",
        scrolled ? "bg-card/95 backdrop-blur-md shadow-sm border-border" : "bg-card border-transparent"
      )}
    >
      <div className="container max-w-[1200px] mx-auto flex items-center justify-between px-4 md:px-8 h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <img src={logoFull} alt="Logo CrossFit Chistera — box affiliée à Saint-Pierre-de-Chandieu" width={183} height={256} {...({ fetchpriority: "high" } as any)} decoding="async" className="h-12 md:h-16 w-auto rounded" />
          <div className="flex flex-col leading-none">
            <span className="font-heading text-lg md:text-xl text-foreground tracking-wider">CROSSFIT CHISTERA</span>
            <span className="text-[10px] md:text-xs text-foreground/80 font-body normal-case tracking-wide">Saint-Pierre-de-Chandieu</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.href}
              onClick={() => handleNav(item)}
              className={cn("px-3 py-2 text-sm font-medium hover:text-gold transition-colors rounded-md", item.type === "link" && location.pathname === item.href ? "text-gold" : "text-foreground/80")}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Social + CTA + burger */}
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2">
            <a href="https://www.facebook.com/share/1DyTHxfnuD/" target="_blank" rel="noopener noreferrer" className="text-foreground/60 hover:text-gold transition-colors" aria-label="Facebook">
              <Facebook size={18} />
            </a>
            <a href="https://www.instagram.com/crossfitchistera?igsh=bzJocjNtZGI1cDF3" target="_blank" rel="noopener noreferrer" className="text-foreground/60 hover:text-gold transition-colors" aria-label="Instagram">
              <Instagram size={18} />
            </a>
          </div>
          <Button
            onClick={() => handleNav({ label: "Contact", href: "#contact", type: "hash" })}
            className="hidden sm:inline-flex bg-cta text-cta-foreground hover:bg-cta/90 rounded-lg font-semibold text-sm"
          >
            Réserver mon essai gratuit
          </Button>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-foreground"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden bg-card border-t border-border">
          <nav className="flex flex-col py-4 px-6 gap-1">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => handleNav(item)}
                className="py-3 text-left text-base font-medium text-foreground/80 hover:text-gold transition-colors border-b border-border last:border-0"
              >
                {item.label}
              </button>
            ))}
            <div className="flex items-center gap-4 mt-3">
              <a href="https://www.facebook.com/share/1DyTHxfnuD/" target="_blank" rel="noopener noreferrer" className="text-foreground/60 hover:text-gold transition-colors" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href="https://www.instagram.com/crossfitchistera?igsh=bzJocjNtZGI1cDF3" target="_blank" rel="noopener noreferrer" className="text-foreground/60 hover:text-gold transition-colors" aria-label="Instagram">
                <Instagram size={20} />
              </a>
            </div>
            <Button
              onClick={() => handleNav({ label: "Contact", href: "#contact", type: "hash" })}
              className="mt-4 bg-cta text-cta-foreground hover:bg-cta/90 rounded-lg font-semibold"
            >
              Réserver mon essai gratuit
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
